import React, { useEffect, useState } from 'react';
import { AtlasProvider, useAtlas } from '../../context/AtlasContext';
import { useProject } from '../../context/ProjectContext';
import { useProjectPermissions } from '../../hooks/useProjectPermissions';
import { useIdea } from '../../context/IdeaContext';
import { AtlasCanvas } from './AtlasCanvas';
import { AtlasToolbar } from './AtlasToolbar';
import { AtlasSidebar } from './AtlasSidebar';
import { LocationCreateModal } from './LocationCreateModal';
import { MapPinIcon, FolderIcon } from '@phosphor-icons/react';
import { ProductionBar } from '../ProductionBar';

// Inner component that uses the Atlas context
const AtlasPageInner: React.FC = () => {
    const { selectedProjectId, projects } = useProject();
    const permissions = useProjectPermissions(selectedProjectId || undefined);
    const { currentIdea, loadIdeaByProjectId, createIdea } = useIdea();
    const {
        setCurrentIdeaId,
        loadLocations,
        loadMapBlocks,
        placedLocations,
        isLoading,
        isCreateModalOpen,
        setIsCreateModalOpen,
        pendingPlacement,
        setPendingPlacement,
        setViewportState
    } = useAtlas();

    const [isInitializing, setIsInitializing] = useState(false);

    // Initialize: Load or create Idea for the project, then use Idea ID for Atlas
    useEffect(() => {
        const initAtlas = async () => {
            if (!selectedProjectId) return;
            // Skip if we already have the right idea loaded
            if (currentIdea?.project_id === selectedProjectId) {
                setCurrentIdeaId(currentIdea.id);
                loadLocations(currentIdea.id);
                loadMapBlocks(currentIdea.id);
                return;
            }

            setIsInitializing(true);
            try {
                // Try to load existing idea for this project
                let idea = await loadIdeaByProjectId(selectedProjectId);

                // If no idea exists, create one
                if (!idea) {
                    const project = projects.find(p => p.id === selectedProjectId);
                    idea = await createIdea(project?.title || 'Untitled Idea', selectedProjectId);
                }

                // Now use the actual idea ID (not project ID) for Atlas
                if (idea) {
                    setCurrentIdeaId(idea.id);
                    loadLocations(idea.id);
                    loadMapBlocks(idea.id);
                }
            } finally {
                setIsInitializing(false);
            }
        };

        initAtlas();
    }, [selectedProjectId, currentIdea?.id, currentIdea?.project_id, loadIdeaByProjectId, createIdea, projects, setCurrentIdeaId, loadLocations, loadMapBlocks]);

    // Handle empty state click
    const handleEmptyStateClick = () => {
        // Open modal for creating first location at center
        setPendingPlacement({ x: 0, y: 0 });
        setIsCreateModalOpen(true);
    };

    // Center viewport on origin when first location is placed
    // The canvas uses a 5000px offset, so we need to position the viewport
    // such that (0, 0) in logical coords is centered in view
    React.useEffect(() => {
        if (placedLocations.length === 1 && !isLoading) {
            // After first location is placed, center the viewport on it
            // The location at (0, 0) is rendered at canvas position (5000, 5000)
            // We want to center this in the view
            // viewportState.positionX/Y represent the pan offset
            // To center on (5000, 5000), we need to offset by -(5000 - viewWidth/2)
            // Approximate centering: set position to show origin area
            setViewportState({
                positionX: -4900, // Offset to bring origin into view
                positionY: -4900,
                scale: 1
            });
        }
    }, [placedLocations.length, isLoading, setViewportState]);

    // Show empty state if no placed locations
    const showEmptyState = !isLoading && placedLocations.length === 0;

    // Show "select project" state if no project is selected
    if (!selectedProjectId) {
        return (
            <div className="flex-1 flex items-center justify-center bg-zinc-950">
                <div className="text-center max-w-sm">
                    <FolderIcon className="w-12 h-12 text-zinc-700 mx-auto mb-4" weight="duotone" />
                    <p className="text-sm text-zinc-400 mb-2">No project selected</p>
                    <p className="text-xs text-zinc-600">
                        Select a project from the sidebar to start building your world atlas.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col w-full h-full bg-zinc-950 overflow-hidden">
            {/* Production Bar */}
            <ProductionBar page="atlas" />

            {/* Main Atlas Content */}
            <div className="relative flex-1 overflow-hidden">
            {/* Empty State - Account for sidebar width (w-72 = 288px) */}
            {showEmptyState ? (
                <div className="absolute inset-0 right-72 flex flex-col items-center justify-center z-10">
                    {/* Grid Background - extends under sidebar */}
                    <div
                        className="absolute inset-0 -right-72"
                        style={{
                            backgroundImage: `
                                linear-gradient(to right, #1a1a1a 1px, transparent 1px),
                                linear-gradient(to bottom, #1a1a1a 1px, transparent 1px)
                            `,
                            backgroundSize: '24px 24px'
                        }}
                    />

                    {/* Center Content - centered in visible canvas area (excluding sidebar) */}
                    <button
                        onClick={handleEmptyStateClick}
                        className="relative flex flex-col items-center gap-4 p-8 rounded-2xl hover:bg-white/5 transition-colors group cursor-pointer"
                    >
                        <div className="w-24 h-24 bg-zinc-800/50 rounded-full flex items-center justify-center group-hover:bg-zinc-700/50 transition-colors">
                            <MapPinIcon className="w-12 h-12 text-zinc-500 group-hover:text-zinc-400 transition-colors" weight="duotone" />
                        </div>
                        <p className="text-sm text-zinc-500 group-hover:text-zinc-400 transition-colors">
                            Click to create your first location
                        </p>
                    </button>
                </div>
            ) : (
                <>
                    {/* Main Canvas */}
                    <AtlasCanvas readOnly={!permissions.canEdit} />

                    {/* Left Toolbar - Only show when user can edit */}
                    {permissions.canEdit && <AtlasToolbar />}
                </>
            )}

            {/* Right Sidebar */}
            <AtlasSidebar readOnly={!permissions.canEdit} />

            {/* Location Create Modal - Only show when user can edit */}
            {isCreateModalOpen && permissions.canEdit && (
                <LocationCreateModal
                    placement={pendingPlacement}
                    onClose={() => {
                        setIsCreateModalOpen(false);
                        setPendingPlacement(null);
                    }}
                />
            )}

            {/* Loading Overlay */}
            {isLoading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-zinc-400">Loading atlas...</span>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
};

// Main component with provider wrapper
export const AtlasPage: React.FC = () => {
    return (
        <AtlasProvider>
            <AtlasPageInner />
        </AtlasProvider>
    );
};
